/**
 * React Query hooks for Creative Studio operations.
 *
 * Provides hooks for:
 * - Image generation with automatic polling
 * - Multi-turn editing sessions
 * - Saving to Media Library
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import * as creativeStudioApi from '@/services/api/creativeStudio';
import { mediaQueryKeys } from './useMediaQueries';
import type {
  GenerateImageRequest,
  StartRefinementSessionRequest,
  ApplyRefinementRequest,
  AddSessionReferenceImageRequest,
  SaveToMediaLibraryRequest,
  GenerationResultResponse,
  RefinementSessionResponse,
  MarkShade,
} from '@/types/creativeStudio';

// =============================================================================
// Query Keys
// =============================================================================

export const creativeStudioQueryKeys = {
  all: ['creative-studio'] as const,
  job: (jobId: string) => [...creativeStudioQueryKeys.all, 'job', jobId] as const,
  refinement: (sessionId: string) => [...creativeStudioQueryKeys.all, 'refinement', sessionId] as const,
  presets: () => [...creativeStudioQueryKeys.all, 'presets'] as const,
  aspectRatios: () => [...creativeStudioQueryKeys.all, 'aspect-ratios'] as const,
  resolutions: () => [...creativeStudioQueryKeys.all, 'resolutions'] as const,
};

// =============================================================================
// Generation Hooks
// =============================================================================

/**
 * Hook to start a new image generation job.
 *
 * Returns a mutation that starts generation and returns a job_id.
 * Use useGenerationResult() to poll for completion.
 */
export function useGenerateImage() {
  return useMutation({
    mutationFn: (params: GenerateImageRequest) => creativeStudioApi.startGeneration(params),
  });
}

/**
 * Hook to poll for generation job results.
 *
 * Automatically polls every 2 seconds until the job is complete or failed.
 * Set enabled to true to start polling, false to stop.
 *
 * @param jobId - The job ID to poll for
 * @param enabled - Whether polling is enabled
 */
export function useGenerationResult(jobId: string | null, enabled: boolean = true) {
  return useQuery<GenerationResultResponse>({
    queryKey: creativeStudioQueryKeys.job(jobId || ''),
    queryFn: () => creativeStudioApi.pollGenerationResult(jobId!),
    enabled: !!jobId && enabled,
    refetchInterval: (query) => {
      const data = query.state.data;
      // Stop polling when complete or failed
      if (data?.status === 'completed' || data?.status === 'failed') {
        return false;
      }
      return 2000; // Poll every 2 seconds
    },
    // Disable stale time to ensure fresh data on each poll
    staleTime: 0,
    // Keep data available even when not polling
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
}

// =============================================================================
// Refinement Session Hooks
// =============================================================================

/**
 * Hook to end a refinement session.
 */
export function useEndSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sessionId: string) => creativeStudioApi.endSession(sessionId),
    onSuccess: (_, sessionId) => {
      queryClient.removeQueries({ queryKey: creativeStudioQueryKeys.refinement(sessionId) });
    },
  });
}

/**
 * Hook to start a multi-turn refinement session.
 *
 * The Original image comes from one of two sources — pass exactly one of
 * `generation_job_id` or `media_image_id` (enforced by the backend's
 * StartRefinementSessionRequest validator):
 *
 * - `generation_job_id` — refine an image just produced by /generate.
 *   The recommended post-generation iteration flow:
 *     1. Generate an image with a prompt (optionally with a reference image)
 *     2. If not satisfied, use this hook to start refining
 *     3. Use useApplyRefinement() to apply critiques
 *     4. Repeat until satisfied, then save to Media Library
 * - `media_image_id` — refine an existing Media Library image (the
 *   "Edit in Studio" path from ImageDetailModal). Starts with no inherited
 *   reference images and an empty original_prompt; the user supplies
 *   critique via useApplyRefinement() as usual.
 */
export function useStartRefinementSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: StartRefinementSessionRequest) =>
      creativeStudioApi.startRefinementSession(params),
    onSuccess: (data) => {
      // Cache the refinement session data
      queryClient.setQueryData(creativeStudioQueryKeys.refinement(data.session_id), data);
    },
  });
}

/**
 * Hook to apply a refinement critique to the current image.
 *
 * The model receives:
 * - All named reference images attached to the session, in order
 * - Current generated image (last)
 * - Original prompt context
 * - Your critique/refinement instruction
 *
 * The critique may refer to any attached image by its name (e.g.,
 * "place the logo top-right"). Manage attached references via
 * useAddSessionReferenceImage / useRemoveSessionReferenceImage /
 * useAddSessionReferenceMark.
 */
export function useApplyRefinement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      params,
    }: {
      sessionId: string;
      params: ApplyRefinementRequest;
    }) => creativeStudioApi.applyRefinement(sessionId, params),
    onSuccess: (data) => {
      // Update the cached refinement session data
      queryClient.setQueryData(creativeStudioQueryKeys.refinement(data.session_id), data);
    },
  });
}

/**
 * Hook to get the current state of a refinement session.
 */
export function useRefinementSession(sessionId: string | null, enabled: boolean = true) {
  return useQuery<RefinementSessionResponse>({
    queryKey: creativeStudioQueryKeys.refinement(sessionId || ''),
    queryFn: () => creativeStudioApi.getRefinementSession(sessionId!),
    enabled: !!sessionId && enabled,
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook to revert a refinement session to a previous state.
 *
 * Use this when a refinement didn't work out and the user wants to try again.
 * All refinements after the target index will be removed.
 */
export function useRevertRefinement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sessionId, targetIndex }: { sessionId: string; targetIndex: number }) =>
      creativeStudioApi.revertRefinement(sessionId, targetIndex),
    onSuccess: (data) => {
      // Update the cached refinement session data
      queryClient.setQueryData(creativeStudioQueryKeys.refinement(data.session_id), data);
    },
  });
}

/**
 * Hook to add a reference image to an active refinement session.
 *
 * Used by the "Add Reference Image" affordance in RefinementPanel. On success
 * the cached session data is updated so the new image appears immediately
 * under "Reference Images (N+1)".
 */
export function useAddSessionReferenceImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      params,
    }: {
      sessionId: string;
      params: AddSessionReferenceImageRequest;
    }) => creativeStudioApi.addSessionReferenceImage(sessionId, params),
    onSuccess: (data) => {
      queryClient.setQueryData(creativeStudioQueryKeys.refinement(data.session_id), data);
    },
  });
}

/**
 * Hook to remove a reference image from an active refinement session.
 */
export function useRemoveSessionReferenceImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      mediaImageId,
    }: {
      sessionId: string;
      mediaImageId: string;
    }) => creativeStudioApi.removeSessionReferenceImage(sessionId, mediaImageId),
    onSuccess: (data) => {
      queryClient.setQueryData(creativeStudioQueryKeys.refinement(data.session_id), data);
    },
  });
}

/**
 * Hook to add a session-only reference mark (annotation overlay) to an
 * active refinement session. The blob is a transparent PNG containing
 * ONLY the user-drawn colored stroke overlay — no base image pixels. The
 * backend composites it onto the session image at native resolution,
 * stores the composite under a session-scoped GCS path, and appends it to
 * the session's reference list so the next refinement can locate it by
 * name (`ref-mark-N`).
 */
export function useAddSessionReferenceMark() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      blob,
      markShade,
      bbox,
    }: {
      sessionId: string;
      blob: Blob;
      markShade: MarkShade;
      bbox?: {
        x_frac: number;
        y_frac: number;
        w_frac: number;
        h_frac: number;
      } | null;
    }) =>
      creativeStudioApi.addSessionReferenceMark(sessionId, blob, markShade, bbox ?? null),
    onSuccess: (data) => {
      queryClient.setQueryData(creativeStudioQueryKeys.refinement(data.session_id), data);
    },
  });
}

// =============================================================================
// Save to Media Library Hook
// =============================================================================

/**
 * Hook to save a generated/edited image to the Media Library.
 */
export function useSaveToMediaLibrary() {
  const queryClient = useQueryClient();
  const memberUuid = useAuthStore((state) => state.memberUuid) || '';

  return useMutation({
    mutationFn: (params: SaveToMediaLibraryRequest) =>
      creativeStudioApi.saveToMediaLibrary(params),
    onSuccess: () => {
      // Invalidate media library queries to show the new image
      queryClient.invalidateQueries({ queryKey: mediaQueryKeys.images(memberUuid) });
      queryClient.invalidateQueries({ queryKey: mediaQueryKeys.storage(memberUuid) });
    },
  });
}

// =============================================================================
// Configuration Hooks
// =============================================================================

/**
 * Hook to get available ad format presets.
 */
export function useAdFormatPresets() {
  return useQuery({
    queryKey: creativeStudioQueryKeys.presets(),
    queryFn: () => creativeStudioApi.getPresets(),
    staleTime: Infinity, // Presets don't change
  });
}

/**
 * Hook to get available aspect ratios.
 */
export function useAspectRatios() {
  return useQuery({
    queryKey: creativeStudioQueryKeys.aspectRatios(),
    queryFn: () => creativeStudioApi.getAspectRatios(),
    staleTime: Infinity,
  });
}

/**
 * Hook to get available resolutions.
 */
export function useResolutions() {
  return useQuery({
    queryKey: creativeStudioQueryKeys.resolutions(),
    queryFn: () => creativeStudioApi.getResolutions(),
    staleTime: Infinity,
  });
}
